// Properties.js
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import DataTable from "../../components/DataTable";
import { Button, Input } from "@material-tailwind/react";
import SoldStatusTag from "../../components/SoldStatusTag";
import SaleDialog from "./../../components/SaleDialog";
import CancellationModal from "./CancellationModal";
import { FormModal } from "../../components/form";
import {
    fetchProjects,
    fetchPropertyConfigurations,
} from "../../services/projects";
import { Tooltip } from "@material-tailwind/react";
import { getCustomerSales } from "../../services/sales";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useUser } from "../../context/UserContext";
import usePermission from "../../hooks/usePermission";
import { PERMISSIONS } from "../../constants/permissions";
import useSubscriptionFeatures from "../../hooks/useSubscriptionFeatures";
import { SUB_FEATURES } from "../../constants/subscriptionFeatures";
import {
    DarkNavButton,
    IconButton,
    OutlineButton,
    SecondaryButton,
} from "../../components/Buttons";
import { History, Plus } from "lucide-react";
import CustomInput from "../../components/CustomInput";
import CostSheetFormModal from "./SoldpropertyTabs/Forms/CostSheetFormModal";
import CostSheetHistoryModal from "../../components/CostSheetHistoryModal";
import axiosInstance from "../../services/api/axios";
import usePagination from "../../hooks/usePagination";
import PaginationControls from "../../components/PaginationControls";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import SearchInput from "../../components/SearchInput";

const filterSaleData = (sales, propertyId) => {


    if (!sales || !Array.isArray(sales) || sales.length === 0) {

        return null;
    }

    // **First, filter by propertyId**
    const propertySales = sales.filter(sale => sale.propertyData.propertyId === propertyId);


    if (propertySales.length === 0) {

        return null;
    }

    // Now prioritize based on cancellationStatus within the filtered propertySales
    const nullCancellationSales = propertySales.filter(sale => sale.cancellationStatus === null);


    const initiatedCancellationSales = propertySales.filter(sale => sale.cancellationStatus === "Initiated");


    const otherCancellationSales = propertySales.filter(sale =>
        sale.cancellationStatus !== null && sale.cancellationStatus !== "Initiated"
    );


    if (nullCancellationSales.length > 0) {

        return nullCancellationSales[0];
    } else if (initiatedCancellationSales.length > 0) {

        return initiatedCancellationSales[0];
    } else if (otherCancellationSales.length > 0) {

        return otherCancellationSales[0];
    }


    return null;
};


const Properties = () => {
    const { userInfo } = useUser();
    const { can, isDeveloperAdmin, isChannelPartner } = usePermission();
    const { hasFeature } = useSubscriptionFeatures();
    const navigate = useNavigate();
    // Cost-sheet creation is a CRM document workflow — hide the row-level
    // "Create" button on BuildVizer Plus. The history viewer stays visible
    // so existing sheets remain inspectable.
    const canCreateCostSheet = hasFeature(SUB_FEATURES.CUSTOMER_DOCUMENTS);
    
    // Filters state
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState("");
    const [configs, setConfigs] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState("");
    const [selectedPhase, setSelectedPhase] = useState("");
    const [phases, setPhases] = useState([]);
    const [propertyStatusFilter, setPropertyStatusFilter] = useState("");
    
    // Modal and dialog state
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
    const [selectedCancelProperty, setSelectedCancelProperty] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState("");
    const [refresh, setRefresh] = useState(false);
    
    // Cost Sheet Modal State
    const [sendLoading, setSendLoading] = useState(false);
    
    // Cost Sheet Modal State
    const [isCostSheetModalOpen, setIsCostSheetModalOpen] = useState(false);
    const [selectedCostSheetProperty, setSelectedCostSheetProperty] = useState(null);
    const [approver, setApprover] = useState(null);
    const [comment, setComment] = useState('');
    const [costSheetData, setCostSheetData] = useState({});
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [costSheetTotals, setCostSheetTotals] = useState({});
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [selectedHistoryProperty, setSelectedHistoryProperty] = useState(null);

    // Create fetch function for properties with pagination
    const fetchPropertiesBackend = useCallback(async ({ page, page_size, search }) => {
        let customerId = null;
        let loginId = null;

        // Sales users see only properties tied to their own login; developer admins / others see customer-wide.
        // Detect "sales-like" by having sale.read but not being a developer admin.
        const isSalesScoped = !isDeveloperAdmin()
            && can(PERMISSIONS.SALE_READ)
            && !can(PERMISSIONS.PROJECT_CREATE);
        if (userInfo && isSalesScoped) {
            loginId = userInfo?.loginId;
        } else if (userInfo && userInfo.loginIdentifierDetails?.customer?.customerId) {
            customerId = userInfo.loginIdentifierDetails.customer.customerId;
        } else {
            console.warn("Could not determine user identifier.");
            return { items: [], total: 0, page: 1, page_size: 50, total_pages: 1, has_next: false, has_previous: false };
        }

        const params = { page, page_size };
        if (customerId) params.customerId = customerId;
        if (loginId) params.loginId = loginId;
        if (search) params.search = search;
        if (selectedProject) params.projectId = selectedProject;
        if (selectedPhase) params.projectPhaseId = selectedPhase;
        if (propertyStatusFilter) params.status = propertyStatusFilter;

        const response = await axiosInstance.get('/property', { params });
        
        // Return the response (backend should already be in paginated format)
        // If backend is not paginated yet, transform it here
        if (response.items && response.total !== undefined) {
            // Already paginated format
            return response;
        } else if (Array.isArray(response)) {
            // Old format - transform to paginated format
            return {
                items: response,
                total: response.length,
                page: 1,
                page_size: response.length,
                total_pages: 1,
                has_next: false,
                has_previous: false
            };
        }
        
        return { items: [], total: 0, page: 1, page_size: 50, total_pages: 1, has_next: false, has_previous: false };
    }, [userInfo, selectedProject, selectedPhase, propertyStatusFilter, can, isDeveloperAdmin]);

    // Use the pagination hook with manual search
    const {
        data: properties,
        pagination,
        searchInput,
        loading,
        error,
        goToPage,
        changePageSize,
        handleSearchInputChange,
        triggerSearch,
        refetch
    } = usePagination(fetchPropertiesBackend, 30, '', true, true);

    // Refetch when filters change
    useEffect(() => {
        refetch();
    }, [selectedProject, selectedPhase, propertyStatusFilter, refetch]);

    const handleLoginIdSelect = (loginId) => {
        setApprover(loginId);
    };

    const handleCostSheetComment = (comment) => {
        setComment(comment);
    };

    //fetch cutsomers
    useEffect(() => {
        const getCustomers = async () => {
            try {
                const response = await axiosInstance.get(`customers/user`, {
                    params: {
                        customerId: userInfo.loginIdentifierDetails.customer.customerId,
                    },
                });

                // Handle both paginated and non-paginated responses
                const customersArray = response?.items || (Array.isArray(response) ? response : []);
                setCustomers(customersArray);
            } catch (error) {
                console.error("Error fetching customers:", error);
                setCustomers([]);
            }
        };

        getCustomers();
    }, [userInfo]);


    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchProjects({ customerId: userInfo.loginIdentifierDetails?.customer?.customerId });
                // Ensure response is an array
                const projectsArray = Array.isArray(response) ? response : [];
                // Filter out projects where hideCpInventory is true only for AGENT role
                const filteredProjects = isChannelPartner()
                    ? projectsArray.filter(project => !project.configuration?.hideCpInventory)
                    : projectsArray;
                setProjects(filteredProjects);
                console.log("projects", filteredProjects);
            } catch (error) {
                console.error("Error fetching projects:", error);
            }
        };
        fetchData();
    }, [userInfo, isChannelPartner]);

    // Fetch configurations
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                // Send customerId if available
                const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
                const response = await fetchPropertyConfigurations(customerId ? { customerId } : undefined);
                // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
                let configsArray = [];
                if (response?.data?.items && Array.isArray(response.data.items)) {
                    configsArray = response.data.items;
                } else if (response?.items && Array.isArray(response.items)) {
                    configsArray = response.items;
                } else if (Array.isArray(response?.data)) {
                    configsArray = response.data;
                } else if (Array.isArray(response)) {
                    configsArray = response;
                }
                setConfigs(configsArray);
            } catch (error) {
                console.error("Error fetching configurations:", error);
            }
        };
        fetchConfig();
        
    }, [userInfo]);


    useEffect(() => {
        const fetchPhases = async () => {
            if (!selectedProject) {
                setPhases([]);
                return;
            }

            try {
                // Use the new API endpoint and pass projectId as a query parameter
                const response = await axiosInstance.get('/projects/phase', {
                    params: {
                        projectId: selectedProject
                    }
                });
                // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
                let phasesArray = [];
                if (response?.data?.items && Array.isArray(response.data.items)) {
                    phasesArray = response.data.items;
                } else if (response?.items && Array.isArray(response.items)) {
                    phasesArray = response.items;
                } else if (Array.isArray(response?.data)) {
                    phasesArray = response.data;
                } else if (Array.isArray(response)) {
                    phasesArray = response;
                }
                setPhases(phasesArray);
            } catch (error) {
                console.error("Error fetching phases:", error);
                setPhases([]);
            }
        };

        fetchPhases();
       
        setSelectedPhase("");
    }, [selectedProject]);

    const filteredConfigs = useMemo(() => {
        return selectedProject
            ? configs.filter((config) => config.projectId === selectedProject)
            : configs;
    }, [selectedProject, configs]);

    // Note: Client-side filtering is now handled by the backend through the fetchPropertiesBackend function
    // This reduces load on the client and improves performance for large datasets



    const navigateToSoldDetails = async (property) => {
        try {
            const salesResponse = await getCustomerSales({
                propertyId: property.propertyId,
                customerId: userInfo.loginIdentifierDetails.customer.customerId,
            });
            let saleDataDetails = null;
            const { propertyId } = property.propertyData;

            if (salesResponse && Array.isArray(salesResponse)) {
                saleDataDetails = filterSaleData(salesResponse, propertyId);


                if (saleDataDetails) {

                    navigate(`/properties/sold/${saleDataDetails.customerSalesId}?projectId=${property.propertyData.projectPhase.project.projectId}`);
                } else {
                    console.error(
                        "No valid sale data found for this property:",
                        property
                    );
                    toast.error("No valid sale data found for this property.");
                }
            } else {
                console.error("No sale data found for this property:", property);
                toast.error("No sale data found for this property.");
            }
        } catch (error) {
            console.error("Error fetching sales:", error);
            toast.error("Error fetching sale details for this property");
        }
    };


    const closeDialog = () => {
        setIsDialogOpen(false);
        setSelectedStatus("");
    };

    const closeCancellationModal = () => {
        setIsCancellationModalOpen(false);
        setSelectedCancelProperty(null);
    };

    const handleViewDetails = async (property) => {

        setSelectedProperty(property.propertyData);
        setSelectedStatus(property.propertyData.status);
        const { propertyId } = property.propertyData; // Get propertyId from propertyData
        if (property.propertyData.status.toLowerCase() === "sold") {
            try {
                const salesResponse = await getCustomerSales({
                    customerId: userInfo.loginIdentifierDetails.customer.customerId,
                    propertyId: propertyId, // Pass propertyId to API
                });

                const saleDataDetails = filterSaleData(salesResponse, propertyId); // Pass propertyId to filterSaleData

                if (saleDataDetails) {
                    navigate(`/properties/sold/${saleDataDetails.customerSalesId}?projectId=${property.propertyData.projectPhase.project.projectId}`);
                } else {
                    console.error(
                        "No valid sale data found for this property:",
                        property
                    );
                    toast.error("No valid sale data found for this property.");
                }
            } catch (err) {
                console.error("Error fetching sales:", err);
                toast.error("Error fetching sale details for this property");
            }
        } else {
            setIsDialogOpen(true);
        }
    };


    const handleSaleSuccess = () => {
        refetch(); // Refetch properties after sale success
    };
    const handleCustomerIdSelect = (customerId) => {

        setSelectedCustomerId(customerId);
    };

    const handleCustomerSelect = (customer) => {

        setSelectedCustomer(customer);
    };

    const handleSendDocument = async (documentContent) => {
        setSendLoading(true);
        try {
            // Ensure approver is available before sending the data
            if (!approver) {
                toast.error("Please select an approver.");
                setSendLoading(false);
                return;
            }

            const approvalData = {
                approvalTypeId: 1,
                requesterLoginId: userInfo.loginId,
                approverLoginId: approver,  // Use the 'approver' state value
                userId: selectedCustomer?.userId || userInfo.userId, // Use selected customer's userId
                customerId: userInfo.loginIdentifierDetails.customer.customerId,
                projectId: selectedProperty.propertyData.projectPhase.project.projectId,
                propertyId: selectedProperty.propertyData.propertyId,
                status: "PENDING",
                comment: comment || "",
                approvalDatetime: new Date().toISOString().split("T")[0],

                content: documentContent
            };



            const response = await axiosInstance.post('/approvals', approvalData);
            toast.success("Cost sheet sent for approval successfully!");
        } catch (error) {
            console.error("Error sending cost sheet for approval:", error);
            toast.error("Failed to send cost sheet for approval.");
        } finally {
            setSendLoading(false);
        }
    };

    // Cost Sheet Modal Handlers
    const handleOpenCostSheetModal = (property) => {

        setSelectedCostSheetProperty(property);
        setSelectedProperty(property);

        setIsCostSheetModalOpen(true);


        // Initialize cost sheet data when the modal opens
        setCostSheetData({
            name: property.propertyData.projectPhase.project.projectName || "",
            property_type: property.propertyData.propertyType || "",
            property_no: property.propertyData.propertyNo || "",
            building_no: property.propertyData.buildingNo || "",
            floor: property.propertyData.floor || "",
            property_type_desc: property.propertyData.propertyType || "",
            carpetAreaSqmt: property.propertyConfigData?.carpetAreaSqmt || 0,
            carpetAreaSqft: property.propertyConfigData?.carpetAreaSqft || 0,
            balconyAreaSqmt: property.propertyConfigData?.balconyAreaSqmt || 0,
            balconyAreaSqft: property.propertyConfigData?.balconyAreaSqft || 0,
            dryBalconyAreaSqmt: property.propertyConfigData?.dryBalconyAreaSqmt || 0,
            dryBalconyAreaSqft: property.propertyConfigData?.dryBalconyAreaSqft || 0,
            superBuiltupAreaSqmt: property.propertyConfigData?.superBuiltupAreaSqmt || 0,
            superBuiltupAreaSqft: property.propertyConfigData?.superBuiltupAreaSqft || 0,
            base_price: property.propertyConfigData?.basePrice || 0,
            floor_rise: property.metadata?.floorRise || 0,
            stamp_duty: property.propertyConfigData?.stampDuty || 0,
            registration_price: property.propertyConfigData?.registrationPrice || 0,
            gst: property.propertyConfigData?.gst || 0,
            cost_sheet_terms: property.propertyConfigData?.costSheetTerms || "",

        });


    };

    const handleCloseCostSheetModal = () => {
        setIsCostSheetModalOpen(false);
        setSelectedCostSheetProperty(null);
    };

    // Cost Sheet History Modal Handlers
    const handleOpenHistoryModal = (property) => {
        setSelectedHistoryProperty(property);
        setIsHistoryModalOpen(true);
    };

    const handleCloseHistoryModal = () => {
        setIsHistoryModalOpen(false);
        setSelectedHistoryProperty(null);
    };

    const showSaleColumns = can(PERMISSIONS.SALE_READ) || isDeveloperAdmin();

    const columns = useMemo(
        () => {
            const base = [
                {
                    key: "project",
                    header: "Project",
                    width: 180,
                    render: (r) => (
                        <span className="text-sm font-medium text-slate-900 truncate block"
                            title={r?.propertyData?.projectPhase?.project?.projectName}>
                            {r?.propertyData?.projectPhase?.project?.projectName || "—"}
                        </span>
                    ),
                },
                {
                    key: "phase",
                    header: "Phase",
                    width: 160,
                    render: (r) => (
                        <span className="text-sm text-slate-700 truncate block"
                            title={r?.propertyData?.projectPhase?.name}>
                            {r?.propertyData?.projectPhase?.name || "—"}
                        </span>
                    ),
                },
                {
                    key: "config",
                    header: "Config",
                    width: 140,
                    render: (r) => (
                        <span className="text-sm text-slate-700 truncate block">
                            {r?.propertyConfigData?.configName || "—"}
                        </span>
                    ),
                },
                {
                    key: "type",
                    header: "Type",
                    width: 120,
                    render: (r) => (
                        <span className="text-sm text-slate-700 truncate block">
                            {r?.propertyData?.propertyType || "—"}
                        </span>
                    ),
                },
                {
                    key: "buildingNo",
                    header: "Bldg.",
                    width: 80,
                    render: (r) => (
                        <span className="text-sm text-slate-700 tabular-nums">
                            {r?.propertyData?.buildingNo || "—"}
                        </span>
                    ),
                },
                {
                    key: "saleType",
                    header: "Sale type",
                    width: 130,
                    render: (r) => (
                        <span className="text-sm text-slate-700 truncate block">
                            {r?.propertyData?.propertySaleType || "—"}
                        </span>
                    ),
                },
                {
                    key: "propertyNo",
                    header: "Unit",
                    width: 80,
                    render: (r) => (
                        <span className="text-sm font-semibold text-slate-900 tabular-nums">
                            {r?.propertyData?.propertyNo || "—"}
                        </span>
                    ),
                },
                {
                    key: "status",
                    header: "Status",
                    width: 110,
                    render: (r) => {
                        const v = r?.propertyData?.status;
                        const cls =
                            v === "Sold"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : v === "Unsold"
                                ? "bg-rose-50 text-rose-700 border-rose-200"
                                : "bg-slate-100 text-slate-700 border-slate-200";
                        return (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${cls}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${
                                    v === "Sold" ? "bg-emerald-500"
                                    : v === "Unsold" ? "bg-rose-500"
                                    : "bg-slate-400"
                                }`} />
                                {v || "—"}
                            </span>
                        );
                    },
                },
            ];

            if (showSaleColumns) {
                // Cost-sheet column is CRM document workflow — only mounted
                // when the tenant's plan unlocks it. BuildVizer Plus tenants
                // get a table without this column at all (cleaner than a
                // half-empty column where every cell is greyed out).
                if (canCreateCostSheet) {
                    base.push({
                        key: "costSheet",
                        header: "Cost sheet",
                        width: 180,
                        align: "center",
                        render: (r) => (
                            <div className="flex items-center justify-center gap-1.5">
                                {r.propertyData.status !== "Sold" && (
                                    <button
                                        type="button"
                                        onClick={() => handleOpenCostSheetModal(r)}
                                        className="inline-flex items-center gap-1 px-2 h-8 rounded-md border border-slate-200 bg-white text-flamingo-600 text-xs font-medium hover:bg-flamingo-50 hover:border-flamingo-200 transition-colors"
                                        title="Create cost sheet"
                                    >
                                        <Plus size={14} /> Create
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => handleOpenHistoryModal(r)}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
                                    title="View cost sheet history"
                                >
                                    <History size={14} />
                                </button>
                            </div>
                        ),
                    });
                }
                base.push({
                    key: "actions",
                    header: "Action",
                    width: 130,
                    align: "center",
                    render: (r) => (
                        r.propertyData.status === "Sold" ? (
                            <button
                                type="button"
                                onClick={() => handleViewDetails(r)}
                                className="inline-flex items-center justify-center px-3 h-8 rounded-md text-xs font-semibold text-slate-700 border border-slate-300 hover:bg-slate-50 transition-colors"
                            >
                                View sale
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => handleViewDetails(r)}
                                className="inline-flex items-center justify-center px-3 h-8 rounded-md text-xs font-semibold text-white bg-gradient-to-r from-flamingo-500 to-flamingo-600 hover:from-flamingo-600 hover:to-flamingo-700 shadow-sm"
                            >
                                Create sale
                            </button>
                        )
                    ),
                });
            }

            return base;
        },
        [handleViewDetails, showSaleColumns]
    );

    // modalStyle removed — superseded by FormModal.

    return (
        <div className="p-8">
            <h1 className="text-2xl font-semibold mb-4">Properties Inventory</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="md:col-span-3 flex items-center gap-4 flex-wrap">
                    <SearchInput
                        value={searchInput}
                        onChange={handleSearchInputChange}
                        onSearch={triggerSearch}
                        placeholder="Search properties..."
                        disabled={loading}
                        className="w-64"
                    />

                    {/* Select Project */}
                    <div className="w-64">
                        <select
                            value={selectedProject}
                            onChange={(e) => setSelectedProject(Number(e.target.value))}
                            className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
                        >
                            <option value="">All Projects</option>
                            {projects.map((project) => (
                                <option key={project.projectId} value={project.projectId}>
                                    {project.projectName}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Select Phase */}
                    <div className="w-64">
                        <select
                            value={selectedPhase}
                            onChange={(e) => setSelectedPhase(e.target.value)}
                            className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
                            disabled={!selectedProject}
                        >
                            <option value="">All Phases</option>
                            {phases.map((phase) => (
                                <option key={phase.projectPhaseId} value={phase.projectPhaseId}>
                                    {phase.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Add property Status Filter */}
                    <div className="w-64">
                        <select
                            value={propertyStatusFilter}
                            onChange={(e) => setPropertyStatusFilter(e.target.value)}
                            className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
                        >
                            <option value="">All Statuses</option>
                            <option value="Sold">Sold</option>
                            <option value="Unsold">Unsold</option>
                            {/* Add more status options as needed */}
                        </select>
                    </div>


                </div>
            </div>

            {error ? (
                <div className="bg-white rounded-2xl border border-rose-200 shadow-sm px-6 py-10 text-center text-rose-700 text-sm">
                    Error loading properties. Please try again.
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={properties}
                    loading={loading}
                    minWidth={showSaleColumns ? 1380 : 1100}
                    rowKey={(r, i) => r?.propertyData?.propertyId ?? i}
                    emptyState={
                        <div className="px-6 py-16 text-center text-sm text-slate-500">
                            No properties match your filters.
                        </div>
                    }
                />
            )}

            {/* Backend Pagination Controls */}
            {!loading && !error && pagination.total > 0 && (
                <PaginationControls
                    pagination={pagination}
                    onPageChange={goToPage}
                    onPageSizeChange={changePageSize}
                    itemName="properties"
                    variant="list"
                />
            )}

            <SaleDialog
                isOpen={isDialogOpen}
                onClose={closeDialog}
                property={selectedProperty}
                selectedStatus={selectedStatus}
                onSaleSuccess={handleSaleSuccess}
            />
            {selectedCancelProperty && (
                <CancellationModal
                    isOpen={isCancellationModalOpen}
                    onClose={closeCancellationModal}
                    propertyId={selectedCancelProperty?.propertyId}
                    customerSaleId={selectedCancelProperty?.customerSalesId}
                />
            )}

            {/* Cost Sheet modal — body is the bespoke wizard inside
                CostSheetFormModal (4 steps + iframe preview + approval flow).
                Wrapping in the canonical FormModal shell gets us the dark
                slate header + ESC/backdrop close behaviour for free. */}
            {selectedCostSheetProperty && (
                <FormModal
                    open={isCostSheetModalOpen}
                    onClose={handleCloseCostSheetModal}
                    title="Cost Sheet"
                    size="2xl"
                >
                    <CostSheetFormModal
                        costSheetData={costSheetData}
                        isOpen={isCostSheetModalOpen}
                        onClose={handleCloseCostSheetModal}
                        customers={customers}
                        property={selectedProperty}
                        onSendDocument={handleSendDocument}
                        sendLoading={sendLoading}
                        onLoginIdSelect={handleLoginIdSelect}
                        onCommentChange={handleCostSheetComment}
                        onCustomerSelect={handleCustomerSelect}
                        approver={approver}
                        selectedUser={selectedCustomer}
                    />
                </FormModal>
            )}

            {/* Cost Sheet History Modal */}
            <CostSheetHistoryModal
                isOpen={isHistoryModalOpen}
                onClose={handleCloseHistoryModal}
                property={selectedHistoryProperty}
                userInfo={userInfo}
            />
        </div>
    );
};

export default Properties;